import { type QConfigTableData } from '@exam-management/common/types';
import {
  GetQConfigByIdDocument,
  UpdateQuestionQConfigStructureLocationTypeInput,
  useUpdateSingleQuestionQConfigStructureMutation,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useParams } from '@exam-management/router';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Loading,
  Text,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';

import {
  ChapterInfoSection,
  QConfigSingleQuestionConditionsFormFields,
  QuestionSummarySection,
  SingleQuestionConditionsTable,
} from '../../components';
import { useFetchLos, useGetPropertiesCount, useGetSelectedLOS } from '../../hooks';
import {
  createQuestionTypeSchemaByQuestionCount,
  useAddSingleQuestionConditions,
  useAddSingleQuestionConditionsFormContext,
  type AddSingleQuestionConditionsFormType,
} from '../../providers';
import type { ModalContentMapType } from '../../types';
import { CONTENT_TYPE_TEXT_MAP } from '../../utils';

interface UpdateSingleQuestionContentProps {
  onClose: () => void;
  questionTypeData: QConfigTableData;
  setModalContent: (content: keyof ModalContentMapType) => void;
}

export default function UpdateSingleQuestionContent(
  props: Readonly<UpdateSingleQuestionContentProps>,
) {
  const { onClose, setModalContent, questionTypeData } = props;
  const {
    chapterData,
    questionCount,
    levelItemsWithLevel,
    defaultLOS,
    structures: qConfigStructures,
  } = useAddSingleQuestionConditions();
  const { getValues, watch, handleSubmit, clearErrors, setError, formState } =
    useAddSingleQuestionConditionsFormContext();
  const { showSnackbar } = useSnackbar();
  const { dirtyFields, isValid, isSubmitting } = formState;
  const { qconfigId: qConfigId } = useParams('/exam-courses/:courseId/qconfigs/:qconfigId/details');
  const { contentType, location, los } = watch();
  const subtopics = watch('location.subtopics');
  const selectedSubtopics = Object.entries(subtopics ?? {})
    .filter(([, value]) => value)
    .map(([key]) => key);

  const existingLOS = useGetSelectedLOS({
    chapterId: chapterData.id,
    qConfigStructures,
    defaultLOS,
  });
  const selectedLOS = los?.value ? [los.value] : [];

  const isLocationSelected = selectedSubtopics.length > 0 || selectedLOS.length > 0;

  const questionTypeError = formState.errors.questionType?.root?.message;

  const { propertyTagMapper, isGettingQuestionPropertiesCount, singleQuestionProperties } =
    useGetPropertiesCount({
      qConfigId,
      qConfigStructureId: chapterData.id,
      selectedSubtopics,
      selectedLOS,
      locationSelected: isLocationSelected,
    });

  const [updateSingleQuestionQConfigStructure] = useUpdateSingleQuestionQConfigStructureMutation({
    onCompleted: () => {
      showSnackbar({
        message: `${contentTypeText} has been saved`,
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: `Unable to Save ${contentTypeText}`,
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId,
          },
        },
      },
    ],
  });

  const handleSave = async (data: AddSingleQuestionConditionsFormType) => {
    const qBankStructureLevelItemIds = Object.keys(data.location.subtopics).filter(
      (key) => data.location.subtopics[key],
    );

    const { questionType, scorePerQuestion } = data;

    const getTagId = (key: string) =>
      propertyTagMapper.find((tag) => tag.schemaName === key)?.id as string;

    const conditions = Object.entries(questionType).flatMap(([key, value]) => {
      const isCalculatedTagId = getTagId(key);

      if (!isCalculatedTagId) {
        return [];
      }

      const difficulty = Object.entries(value).map(([difficultyKey, difficultyValue]) => {
        const difficultyTagId = getTagId(difficultyKey);

        if (!difficultyTagId || !difficultyValue) {
          return null;
        }

        return {
          tagIds: [difficultyTagId, isCalculatedTagId],
          needTotal: difficultyValue,
        };
      });

      return difficulty.filter((difficulty) => difficulty !== null);
    });

    const getLocation = () => {
      if (contentType === 'TOPIC') {
        return {
          qBankStructureLevelItemIds,
          locationType: UpdateQuestionQConfigStructureLocationTypeInput.QbankStructureLevelItem,
        };
      } else {
        return {
          losIds: selectedLOS,
          locationType: UpdateQuestionQConfigStructureLocationTypeInput.LearningObjectiveStatement,
        };
      }
    };

    await updateSingleQuestionQConfigStructure({
      variables: {
        updateSingleQuestionQConfigStructureInput: {
          id: questionTypeData.id,
          scorePerQuestion,
          location: getLocation(),
          conditions,
        },
      },
    });

    onClose();
  };

  const tableData = useMemo(() => {
    const result: {
      calculated: { [key in 'easy' | 'medium' | 'hard']?: number };
      notCalculated: { [key in 'easy' | 'medium' | 'hard']?: number };
    } = {
      calculated: {},
      notCalculated: {},
    };

    clearErrors('questionType');

    if (!singleQuestionProperties) {
      return result;
    }

    for (const questionProperty of singleQuestionProperties) {
      const { tags, totalQuestions } = questionProperty;
      const propertyTag = tags.map(
        (tag) => propertyTagMapper.find((propertyTag) => propertyTag.id === tag.id)?.schemaName,
      );

      const key = propertyTag.find((tag) => tag === 'calculated' || tag === 'notCalculated');
      const difficulty = propertyTag.find(
        (tag) => tag === 'easy' || tag === 'medium' || tag === 'hard',
      );

      if (difficulty && key) {
        const oldValue = result[key][difficulty] ?? 0;
        result[key][difficulty] = oldValue + totalQuestions;
      }
    }

    questionCount.set(result);

    const QuestionTypeSchema = createQuestionTypeSchemaByQuestionCount(result);

    QuestionTypeSchema.safeParseAsync(getValues(['questionType'])[0]).then((result) => {
      const resultError = result.error;
      if (resultError) {
        for (const error of resultError.errors) {
          if (error.path.length > 0) {
            setError(`questionType.${error.path.join('.')}` as Parameters<typeof setError>[0], {
              type: 'manual',
              message: error.message,
            });
          }
        }
      }
    });

    return result;
  }, [singleQuestionProperties, propertyTagMapper]);

  const { totalSingleQuestions, selectedQuestions, totalScore } = useMemo(() => {
    const { scorePerQuestion = 0, questionType = {} } = watch();

    const totalSingleQuestions = Object.values(tableData).reduce((acc, difficulty) => {
      return acc + Object.values(difficulty).reduce((acc, value) => acc + value, 0);
    }, 0);

    const selectedQuestions = Object.values(questionType).reduce((acc, difficulty) => {
      if (!difficulty) {
        return acc;
      }

      return (
        acc +
        Object.values(difficulty).reduce((acc, value) => {
          if (!value) {
            return acc;
          }
          return acc + value;
        }, 0)
      );
    }, 0) as number;

    const totalScore = scorePerQuestion * selectedQuestions;

    return {
      totalSingleQuestions,
      selectedQuestions,
      totalScore,
    };
  }, [watch(), tableData]);

  const goToSelectTopicContent = () => {
    setModalContent('SELECT_TOPIC_CONTENT');
  };
  const isDisable =
    (!dirtyFields.questionType &&
      !dirtyFields.scorePerQuestion &&
      !dirtyFields.location &&
      !dirtyFields.los) ||
    (Object.values(watch('questionType.calculated')).every((value) => !value) &&
      Object.values(watch('questionType.notCalculated')).every((value) => !value)) ||
    !isValid;

  const hasViewPermission = useHasPermission(APP_PERMISSIONS.QBANK.LOS.VIEW);

  const { options, isSearchLOSLoading } = useFetchLos({
    qConfigId,
    qConfigStructureId: chapterData.id,
    contentType,
  });

  const noOptionsText = !hasViewPermission
    ? 'ไม่มีสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ'
    : 'ไม่พบข้อมูล';

  const contentTypeText = CONTENT_TYPE_TEXT_MAP[contentType];

  return (
    <>
      <DialogTitle onClose={onClose} loading={isSubmitting}>
        Edit {contentTypeText}
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ height: '600px' }}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <ChapterInfoSection
            title={chapterData?.name ?? 'บท: ไม่ระบุ'}
            qSource={chapterData?.refCode ?? '-'}
          />
          <QConfigSingleQuestionConditionsFormFields
            onEdit={goToSelectTopicContent}
            levelItemsWithLevel={levelItemsWithLevel}
            locationSelected={isLocationSelected}
            contentType={contentType}
            location={location}
            isEditModal={true}
            group={{
              options,
              loading: isSearchLOSLoading,
              noOptionsText,
              hasViewPermission,
              existingLOS,
            }}
          />
          {isLocationSelected ? (
            <>
              <Text variant='body1Semibold'>จำนวนข้อสอบที่มีในคลัง (ตามระดับความยาก)</Text>
              <QuestionSummarySection
                totalSingleQuestions={totalSingleQuestions}
                selectedQuestions={selectedQuestions}
                totalScore={totalScore}
              />
              {isGettingQuestionPropertiesCount ? (
                <Loading
                  sx={{
                    minHeight: '175px',
                  }}
                />
              ) : (
                <>
                  {singleQuestionProperties?.length === 0 ? (
                    <Alert severity='error'>
                      ไม่มีข้อสอบที่ตรงตามเงื่อนไข กรุณาลองใหม่อีกครั้ง
                    </Alert>
                  ) : null}
                  <SingleQuestionConditionsTable data={tableData} />
                  {questionTypeError ? (
                    <Text
                      variant='caption'
                      sx={{
                        color: (theme) => theme.palette.error.main,
                      }}
                    >
                      {questionTypeError}
                    </Text>
                  ) : null}
                </>
              )}
            </>
          ) : null}
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          size='large'
          type='button'
          color='secondary'
          variant='outlined'
        >
          Cancel
        </Button>
        <Button
          size='large'
          type='button'
          onClick={handleSubmit(handleSave)}
          disabled={isDisable}
          loading={isSubmitting}
        >
          Save
        </Button>
      </DialogActions>
    </>
  );
}
